/** localStorage key — never touched by tenant code. */
export const DUID_KEY = 'stay_signals_duid';
/** sessionStorage key. */
export const CID_KEY = 'stay_signals_cid';

/** Cache for in-memory fallback when Web Storage is unavailable. */
let memoryDuid: string | null = null;

function mintUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for ancient browsers: RFC4122 v4-ish from getRandomValues.
  const buf = new Uint8Array(16);
  crypto.getRandomValues(buf);
  // Indices 6 and 8 are always in-bounds (buf is fixed-length 16); the
  // ??-with-0 keeps strict noUncheckedIndexedAccess happy without changing
  // the RFC4122 v4 bit-manipulation.
  buf[6] = ((buf[6] ?? 0) & 0x0f) | 0x40;
  buf[8] = ((buf[8] ?? 0) & 0x3f) | 0x80;
  const h = Array.from(buf, (b) => b.toString(16).padStart(2, '0'));
  return `${h.slice(0, 4).join('')}-${h.slice(4, 6).join('')}-${h.slice(6, 8).join('')}-${h.slice(8, 10).join('')}-${h.slice(10, 16).join('')}`;
}

function safeGet(storage: Storage, key: string): string | null {
  try { return storage.getItem(key); } catch { return null; }
}

function safeSet(storage: Storage, key: string, value: string): void {
  try { storage.setItem(key, value); } catch { /* quota / blocked — ignore */ }
}

/**
 * Read or mint the persistent device uuid. Degrades to an in-memory
 * value consistent within the current page load when localStorage is
 * blocked (Safari private mode, third-party-cookie-strict settings).
 */
export function readDeviceUuid(): string {
  const existing = safeGet(localStorage, DUID_KEY);
  if (existing) return existing;
  if (memoryDuid) return memoryDuid;
  const fresh = mintUuid();
  safeSet(localStorage, DUID_KEY, fresh);
  memoryDuid = fresh;
  return fresh;
}

/** Read the current session's collectionId, or `null` if absent or storage is unreadable. */
export function readCollectionId(): string | null {
  return safeGet(sessionStorage, CID_KEY);
}

/** Persist the collectionId for the rest of the tab's lifetime. Never throws. */
export function writeCollectionId(cid: string): void {
  safeSet(sessionStorage, CID_KEY, cid);
}
